/**
 * Custom hook for authentication operations
 * Wraps Firebase auth services with Redux state management
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './useTypedRedux';
import {
  clearUser,
  setLoading,
  setError,
  setVerificationId,
  clearVerificationId,
  updateUserProfile as updateUserProfileAction,
  selectUser,
  selectIsAuthenticated,
  selectIsAdmin,
  selectAuthLoading,
  selectAuthError,
  selectVerificationId,
} from '@/store/slices/authSlice';
import { clearWishlist } from '@/store/slices/wishlistSlice';
import { clearSavedItems, clearCart, closeCartDrawer } from '@/store/slices/cartSlice';
import { setLoggingOut } from '@/store/slices/uiSlice';
import {
  sendOTP,
  verifyOTP,
  signOut as firebaseSignOut,
  updateUserProfile as updateUserProfileService,
} from '@/services/firebase';
import { ROUTES } from '@/config/routes';
import { UserProfile } from '@/types/user.types';

interface UseAuthReturn {
  // State
  user: UserProfile | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  verificationId: string | null;
  
  // Actions
  sendOtp: (phoneNumber: string) => Promise<boolean>;
  verifyOtp: (otp: string) => Promise<boolean>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<Pick<UserProfile, 'displayName' | 'email'>>) => Promise<boolean>;
  clearError: () => void;
}

/**
 * useAuth - Custom hook for authentication
 * 
 * @example
 * const { user, isAuthenticated, sendOtp, verifyOtp, logout } = useAuth();
 */
export const useAuth = (): UseAuthReturn => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Selectors
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isAdmin = useAppSelector(selectIsAdmin);
  const loading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const storedVerificationId = useAppSelector(selectVerificationId);

  const isLoading = loading === 'loading';

  /**
   * Send OTP to phone number
   */
  const sendOtp = useCallback(async (phone: string): Promise<boolean> => {
    try {
      dispatch(setLoading('loading'));
      dispatch(setError(null));
      
      // Send OTP
      const verificationId = await sendOTP(phone);
      
      // Store verification ID in Redux
      dispatch(setVerificationId(verificationId));
      dispatch(setLoading('succeeded'));
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send OTP';
      dispatch(setError(errorMessage));
      dispatch(setLoading('failed'));
      return false;
    }
  }, [dispatch]);

  /**
   * Verify OTP and sign in
   */
  const verifyOtp = useCallback(async (otp: string): Promise<boolean> => {
    try {
      dispatch(setLoading('loading'));
      dispatch(setError(null));

      // Confirm OTP; the auth state listener (App.tsx) fetches the profile
      // and dispatches setUser, keeping a single source of truth.
      await verifyOTP(otp);
      dispatch(clearVerificationId());

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to verify OTP';
      dispatch(setError(errorMessage));
      dispatch(setLoading('failed'));
      return false;
    }
  }, [dispatch]);

  /**
   * Sign out user
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      dispatch(setLoggingOut(true));

      await firebaseSignOut();

      dispatch(clearUser());
      dispatch(clearWishlist());
      dispatch(clearSavedItems());
      dispatch(clearCart());
      dispatch(closeCartDrawer());
      dispatch(setLoading('idle'));

      await new Promise(resolve => setTimeout(resolve, 1200));

      navigate(ROUTES.HOME, { replace: true });
      dispatch(setLoggingOut(false));
    } catch (err) {
      dispatch(setLoggingOut(false));
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign out';
      dispatch(setError(errorMessage));
      dispatch(setLoading('failed'));
    }
  }, [dispatch, navigate]);

  /**
   * Update user profile (display name, email)
   */
  const updateProfile = useCallback(async (
    updates: Partial<Pick<UserProfile, 'displayName' | 'email'>>
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      await updateUserProfileService(user.id, updates);
      dispatch(updateUserProfileAction(updates));
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      dispatch(setError(errorMessage));
      return false;
    }
  }, [dispatch, user]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch(setError(null));
  }, [dispatch]);

  return {
    // State
    user,
    isAuthenticated,
    isAdmin,
    isLoading,
    error,
    verificationId: storedVerificationId,
    
    // Actions
    sendOtp,
    verifyOtp,
    logout,
    updateProfile,
    clearError,
  };
};
